import { useEffect, useRef } from 'react'

import { usePostHog } from 'posthog-js/react'
import { useSelector } from 'react-redux'

import { env } from '@/core/environments'
import { useUpdateUser } from '@/core/hooks/useUser'
import { useTranslation } from '@/core/i18n/useTranslation'
import { RootState } from '@/core/store'

export const PostHogIdentify = () => {
	const posthog = usePostHog()
	const { currentLanguage } = useTranslation()
	const { user, isAuthenticated } = useSelector(
		(state: RootState) => state.auth
	)
	const lastIdentifiedId = useRef<string | null>(null)

	const getData = () => {
		if (!user) {
			return undefined
		}

		return {
			email: user.email,
			user_name: user.getFullName(),
			user_role: user.role,
			user_phone_number: user.phoneNumber,
			user_created_at: user.createdAt?.toISO(),
			account_id: user.accountId,
			account_name: user.account?.name,
			account_country: user.account?.country,
			account_mrr: user.account?.metadata?.total_mrr,
			account_segmentation: user.account?.metadata?.segmentation,
			account_players_count: user.account?.playersCount,
			account_boxes_count: user.account?.availableBoxes,
			platform_language: currentLanguage?.split('-')[0],
			...(user.resellerSettings?.resellerName && {
				reseller_name: user.resellerSettings.resellerName,
			}),
			...(user.resellerSettings?.resellerId != null && {
				reseller_id: user.resellerSettings.resellerId,
			}),
		}
	}

	const { mutate: updateUser } = useUpdateUser({
		userId: user?.id,
	})

	useEffect(() => {
		// Skip if PostHog is disabled
		if (env.ENABLE_POSTHOG === 'none') {
			return
		}

		if (!posthog) {
			return
		}

		if (isAuthenticated && user) {
			// Check if we should identify based on the integration level
			const shouldIdentify =
				env.ENABLE_POSTHOG === 'all' ||
				(env.ENABLE_POSTHOG === 'partial' && user.account?.test)

			if (!shouldIdentify) {
				return
			}

			const distinctId = user.uuid || user.email || `user-${user.id}`

			const data = getData()

			if (lastIdentifiedId.current !== distinctId) {
				posthog.identify(distinctId, data)
				lastIdentifiedId.current = distinctId

				// As the user is identified and the identification is set, we will check whether the posthogId is the same as the user
				// has. If it is not the same, we will update the posthogId in the API
				if (user.posthogId !== distinctId) {
					updateUser({
						account_id: user.accountId,
						firstname: user.firstname,
						lastname: user.lastname,
						role_id: user.roleId,
						posthog_id: distinctId,
					})
				}
			} else {
				posthog.people?.set?.({
					...data,
				})
			}
		} else if (!isAuthenticated && !user && lastIdentifiedId.current) {
			// Only reset on an actual logout/identity clear, not transient auth-loading states.
			posthog.reset()
			lastIdentifiedId.current = null
		}
	}, [isAuthenticated, posthog, user, currentLanguage])

	return null
}
